/**
 * 三重県の背景ツアー「三重めぐり」コンテンツ。
 *
 * 軸は一日の時間。二見の日の出から、英虞湾の日没へ。
 * 熊野に4か所、伊賀・伊勢志摩・中南勢・北勢に6か所を配している。
 *
 * 伊勢神宮は扱わない。
 *  公式のよくあるご質問に「商用利用の撮影（写真や映像の公開による収益化を含む）」を
 *  固くお断りする、という記載がある。撮影禁止場所がどこかも公式では特定できない。
 *  社殿も宇治橋も五十鈴川の御手洗場も、この作品では素材にしない。
 *
 * 夫婦岩について。
 *  公式は「日の大神と猿田彦大神縁の興玉神石を遥拝する鳥居の役目をしております」と書く。
 *  つまり二つの岩のあいだが鳥居にあたる。絵には別の鳥居を描かせない。
 *  境内の第一鳥居（大正15年）・第二鳥居（昭和30年）は海側の構図には入らない。
 *  近代の石碑・奉献物も著作権が残りうるので画角に入れない。
 *
 * 花の窟は扱わない。三重県公式がイザナミノミコトの「葬地」と明記している。
 *
 * 熊野古道の峠について。
 *  松本峠の等身大の地蔵、通り峠の子安地蔵（嘉永4年建立）は祈りの対象なので描かせない。
 *  馬越峠は石畳と杉林だけで絵になるので、こちらを採った。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 瀞峡の指定名称は三重県の2ページで「特別名勝、天然記念物」と「国の特別史跡」に食い違う。
 *    指定名は書かず、峡谷の見た目だけを書く。
 *  - 鬼ヶ城の指定年の表記は「1935/12/24-1958/06/24」で、指定日か追加指定日か判断できない。書かない。
 *  - 海女の人数、磯着や道具の意匠、海女小屋の構造は公式に記載がない。書かない。
 *  - 伊賀上野城の天守は1935年に川崎克が復興した建築。高石垣と堀だけを扱う。
 *  - 拝観料・入山料・祭りの日程など、時間で変わるものは書かない。
 */

import type { StageTour } from '../types';

export const mieSceneryTour: StageTour = {
  scenes: [
    {
      id: 'meotoiwa',
      name: '夫婦岩',
      area: '伊勢市二見町・日の出',
      image: '/assets/scenery/mie/scenes/01_meotoiwa.webp',
      hotspots: [
        {
          id: 'meoto-sunpo',
          x: 0.5,
          y: 0.5,
          title: '大岩9メートル、注連縄35メートル',
          body: '大岩（男岩）は高さ9メートル、小岩（女岩）は高さ4メートル、大注連縄は35メートルあります。',
          source: 'https://futamiokitamajinja.or.jp/meguru/meotoiwa/',
          mapQuery: '夫婦岩',
        },
        {
          id: 'meoto-geshi',
          x: 0.74,
          y: 0.32,
          title: '夏至の御来光',
          body: '夫婦岩の間から拝む御来光は、夏至を中心にした二見浦の醍醐味です。夏至の太陽が顔を現すのは、午前4時30分頃です。',
          source: 'https://futamiokitamajinja.or.jp/shootsunrise/',
          mapQuery: '二見浦 日の出',
        },
        {
          id: 'meoto-torii',
          x: 0.24,
          y: 0.62,
          title: '二つの岩が鳥居になる',
          body: '夫婦岩は、日の大神と猿田彦大神ゆかりの興玉神石を遥拝する鳥居の役目をしています。夫婦岩の中央から差し昇る朝日は、夏至を中心に5月・6月・7月が最も美しく拝されます。',
          source: 'https://futamiokitamajinja.or.jp/meguru/meotoiwa/',
          mapQuery: '二見興玉神社',
        },
      ],
    },
    {
      id: 'magose-toge',
      name: '馬越峠',
      area: '尾鷲市・朝',
      image: '/assets/scenery/mie/scenes/02_magose_toge.webp',
      hotspots: [
        {
          id: 'magose-ishidatami',
          x: 0.5,
          y: 0.56,
          title: '尾鷲の雨から道を守った石畳',
          body: '重厚な自然石が折り重なるように敷き詰められた石畳は、日本でもトップクラスの雨量を誇る尾鷲の雨から道を守ってきました。',
          source: 'https://www.kodo.pref.mie.lg.jp/course/08.html',
          mapQuery: '馬越峠',
        },
        {
          id: 'magose-course',
          x: 0.24,
          y: 0.4,
          title: '約5.2キロ、3時間25分',
          body: '道の駅海山からJR尾鷲駅まで約5.2キロ、歩行時間は3時間25分、散策レベルは二つ星です。',
          source: 'https://www.kodo.pref.mie.lg.jp/course/08.html',
          mapQuery: '熊野古道 馬越峠',
        },
        {
          id: 'magose-isan',
          x: 0.76,
          y: 0.32,
          title: '道の世界遺産',
          body: '世界でも珍しい道の世界遺産「紀伊山地の霊場と参詣道」の一部です。峠からは天狗倉山や便石山への登山コースが整備されています。',
          source: 'https://www.kodo.pref.mie.lg.jp/course/08.html',
          mapQuery: '熊野古道 伊勢路',
        },
      ],
    },
    {
      id: 'shichiri-mihama',
      name: '七里御浜',
      area: '御浜町・午前',
      image: '/assets/scenery/mie/scenes/03_shichiri_mihama.webp',
      hotspots: [
        {
          id: 'shichiri-20km',
          x: 0.5,
          y: 0.56,
          title: '約20キロの砂礫海岸',
          body: '鬼ヶ城から熊野川河口までの約20キロは、滑らかな砂礫海岸がつづく七里御浜です。',
          source: 'https://www.pref.mie.lg.jp/MIDORI/HP/shizen/05789001198.htm',
          mapQuery: '七里御浜',
        },
        {
          id: 'shichiri-kokuritsu',
          x: 0.24,
          y: 0.4,
          title: '吉野熊野国立公園',
          body: '吉野熊野国立公園の一部で、公園の指定日は昭和11年2月1日、面積は17,065ヘクタール（令和5年3月31日現在）です。',
          source: 'https://www.pref.mie.lg.jp/MIDORI/HP/shizen/05789001198.htm',
          mapQuery: '吉野熊野国立公園',
        },
        {
          id: 'shichiri-matsumoto',
          x: 0.76,
          y: 0.3,
          title: '松本峠から見下ろす',
          body: '松本峠の東屋からは、熊野速玉大社がある新宮まで約25キロも続く七里御浜と山並みが一望できます。',
          source: 'https://www.kodo.pref.mie.lg.jp/course/17.html',
          mapQuery: '松本峠 展望',
        },
      ],
    },
    {
      id: 'tsukide-chuokozosen',
      name: '月出の中央構造線露頭',
      area: '松阪市・午前',
      image: '/assets/scenery/mie/scenes/04_tsukide_chuokozosen.webp',
      hotspots: [
        {
          id: 'tsukide-shitei',
          x: 0.5,
          y: 0.5,
          title: '平成14年に天然記念物',
          body: '月出の中央構造線露頭は、平成14年（2002年）12月19日に国の天然記念物に指定されました。',
          source: 'https://www.city.matsusaka.mie.jp/site/culture-info/tsukide-chuokozosen.html',
          mapQuery: '月出の中央構造線',
        },
        {
          id: 'tsukide-ryouke',
          x: 0.26,
          y: 0.44,
          title: '北側は領家帯の圧砕岩',
          body: '中央構造線を境に、北側には西南日本内帯の領家帯の圧砕岩（マイロナイト）が分布しています。',
          source: 'https://www.city.matsusaka.mie.jp/site/culture-info/tsukide-chuokozosen.html',
          mapQuery: '月出 露頭',
        },
        {
          id: 'tsukide-sanbagawa',
          x: 0.74,
          y: 0.58,
          title: '南側は三波川帯の黒色片岩',
          body: '南側には西南日本外帯の三波川帯の黒色片岩が分布しています。断層面は北に約60度傾斜しています。',
          source: 'https://www.city.matsusaka.mie.jp/site/culture-info/tsukide-chuokozosen.html',
          mapQuery: '中央構造線 松阪市',
        },
      ],
    },
    {
      id: 'onigajo',
      name: '鬼ヶ城',
      area: '熊野市・昼',
      image: '/assets/scenery/mie/scenes/05_onigajo.webp',
      hotspots: [
        {
          id: 'onigajo-1200m',
          x: 0.5,
          y: 0.46,
          title: '東西1.2キロの大岸壁',
          body: '石英粗面岩の大岸壁が東西1.2キロにわたって連なり、数回にわたる急激な地盤の隆起のあとを残しています。',
          source: 'https://www.bunka.pref.mie.lg.jp/Miebunka/detail?cls=cult_col01&pkey=0000000011',
          mapQuery: '鬼ヶ城',
        },
        {
          id: 'onigajo-6dan',
          x: 0.24,
          y: 0.6,
          title: '六段の階段状',
          body: '岬の崖は一段の高さ2から4メートルの六段からなる階段状で、各段ごとに波蝕洞窟があります。',
          source: 'https://www.bunka.pref.mie.lg.jp/Miebunka/detail?cls=cult_col01&pkey=0000000011',
          mapQuery: '鬼ヶ城 熊野市',
        },
        {
          id: 'onigajo-kaishoku',
          x: 0.76,
          y: 0.32,
          title: '約1キロ続く海蝕洞',
          body: '海蝕されて、いくつもの海蝕洞が約1キロメートルにわたって連続する奇勝です。国の天然記念物および名勝に指定されています。',
          source: 'https://www.pref.mie.lg.jp/MIDORI/HP/shizen/05789001198.htm',
          mapQuery: '鬼ヶ城 名勝',
        },
      ],
    },
    {
      id: 'shishiiwa',
      name: '獅子岩',
      area: '熊野市・昼過ぎ',
      image: '/assets/scenery/mie/scenes/06_shishiiwa.webp',
      hotspots: [
        {
          id: 'shishi-25m',
          x: 0.5,
          y: 0.46,
          title: '高さ25メートル',
          body: '獅子巌は鬼ヶ城の南方、井戸川河口に近い海浜にある高さ25メートルの石英粗面岩塊で、獅子の横顔に似た形をあらわしています。',
          source: 'https://www.bunka.pref.mie.lg.jp/Miebunka/detail?cls=cult_col01&pkey=0000000011',
          mapQuery: '獅子岩 熊野市',
        },
        {
          id: 'shishi-houkou',
          x: 0.74,
          y: 0.34,
          title: '海に向かってほえる',
          body: 'あたかも獅子が太平洋に向かってほえているように見えます。',
          source: 'https://www.bunka.pref.mie.lg.jp/Miebunka/detail?cls=cult_col01&pkey=0000000011',
          mapQuery: '獅子岩',
        },
        {
          id: 'shishi-kumanonada',
          x: 0.24,
          y: 0.62,
          title: '熊野灘の代表的な景観',
          body: '熊野灘にむかって咆哮する巨大なシシの姿に似た獅子岩は、吉野熊野国立公園を代表する海岸の景観のひとつです。',
          source: 'https://www.pref.mie.lg.jp/MIDORI/HP/shizen/05789001198.htm',
          mapQuery: '熊野市 獅子岩',
        },
      ],
    },
    {
      id: 'sekijuku',
      name: '関宿',
      area: '亀山市・午後',
      image: '/assets/scenery/mie/scenes/07_sekijuku.webp',
      hotspots: [
        {
          id: 'seki-judenken',
          x: 0.5,
          y: 0.5,
          title: '昭和59年に国の選定',
          body: '関宿は昭和59年に国の重要伝統的建造物群保存地区に選定されました。保存地区は東追分と西追分の間、約1.8キロ、25ヘクタールにおよびます。',
          source: 'https://www.city.kameyama.mie.jp/docs/2014112311907/',
          mapQuery: '関宿',
        },
        {
          id: 'seki-4chiku',
          x: 0.24,
          y: 0.38,
          title: '四つの地区',
          body: '木崎・中町・新所・北裏の四つの地区に大きく分かれます。中町には本陣・脇本陣・問屋などが置かれ、町家は比較的建ちの高い大規模なものが多く、二階壁面も塗籠めて虫籠窓をあけたりします。',
          source: 'https://www.city.kameyama.mie.jp/docs/2014112311907/',
          mapQuery: '関宿 中町',
        },
        {
          id: 'seki-kurashi',
          x: 0.76,
          y: 0.64,
          title: '今も人が暮らす',
          body: '関宿は貴重な建造物の保存地区ですが、住人の生活の場でもあります。いまそこで暮らしている人たちが、関宿をより素晴らしい、生きた町並みにしています。',
          source: 'https://www.city.kameyama.mie.jp/docs/2014112312294/',
          mapQuery: '亀山市 関宿',
        },
      ],
    },
    {
      id: 'akame-shijuhachi',
      name: '赤目四十八滝',
      area: '名張市・午後',
      image: '/assets/scenery/mie/scenes/08_akame_shijuhachi.webp',
      hotspots: [
        {
          id: 'akame-hyakusen',
          x: 0.5,
          y: 0.5,
          title: '四つの百選',
          body: '赤目四十八滝は平成の名水百選、日本の滝百選、森林浴の森百選、遊歩百選に選ばれています。',
          source: 'https://kankou-nabari.jp/3620',
          mapQuery: '赤目四十八滝',
        },
        {
          id: 'akame-48',
          x: 0.24,
          y: 0.36,
          title: '四十八は数が多いという意味',
          body: '「四十八」は滝の数が多いことを意味し、阿弥陀仏が四十八願をたてたことに由来するともいわれます。',
          source: 'https://kankou-nabari.jp/3620',
          mapQuery: '赤目四十八滝 荷担滝',
        },
        {
          id: 'akame-oosanshouuo',
          x: 0.76,
          y: 0.64,
          title: 'オオサンショウウオの渓谷',
          body: '室生赤目青山国定公園の中心に位置する約4キロの渓谷で、天然記念物のオオサンショウウオが生息しています。',
          source: 'https://kankou-nabari.jp/3620',
          mapQuery: '赤目渓谷',
        },
      ],
    },
    {
      id: 'dorokyo',
      name: '瀞峡',
      area: '紀宝町・夕方前',
      image: '/assets/scenery/mie/scenes/09_dorokyo.webp',
      hotspots: [
        {
          id: 'doro-fukamidori',
          x: 0.5,
          y: 0.56,
          title: '深緑色の水',
          body: '深緑色の水が、切り立った岩壁とうっそうと茂る原生林を縫うように流れます。',
          source: 'https://www.pref.mie.lg.jp/MIDORI/HP/shizen/05864001243.htm',
          mapQuery: '瀞峡',
        },
        {
          id: 'doro-satsuki',
          x: 0.24,
          y: 0.4,
          title: '岩壁のサツキ',
          body: 'サツキが岸壁にへばりつくように咲き乱れます。',
          source: 'https://www.pref.mie.lg.jp/MIDORI/HP/shizen/05864001243.htm',
          mapQuery: '瀞八丁',
        },
        {
          id: 'doro-sankengan',
          x: 0.76,
          y: 0.32,
          title: '三県にまたがる',
          body: '瀞峡は三重・奈良・和歌山の三県にまたがる、吉野熊野国立公園を代表する自然景観のひとつです。',
          source: 'https://www.pref.mie.lg.jp/MIDORI/HP/shizen/05789001198.htm',
          mapQuery: '瀞峡 紀宝町',
        },
      ],
    },
    {
      id: 'agowan',
      name: '英虞湾 横山展望台',
      area: '志摩市・日没',
      image: '/assets/scenery/mie/scenes/10_agowan.webp',
      hotspots: [
        {
          id: 'ago-shinju',
          x: 0.5,
          y: 0.54,
          title: '真珠の養殖筏',
          body: '真珠養殖で有名な英虞湾を一望できます。複雑に入り組んだリアス海岸と真珠の養殖筏が、自然と人間の活動が融合した景観を見せてくれます。',
          source: 'https://www.env.go.jp/park/iseshima/guide/view.html',
          mapQuery: '横山展望台',
        },
        {
          id: 'ago-rias',
          x: 0.24,
          y: 0.4,
          title: '沈降と隆起がつくった地形',
          body: '志摩半島の海岸は、陸地が長い間に沈降と隆起を繰り返し、かつて川や谷であったところに海水が入り込んでできた、リアス海岸と呼ばれる岬や入江の多い複雑な地形をなしています。',
          source: 'https://www.env.go.jp/park/iseshima/point/index.html',
          mapQuery: '英虞湾',
        },
        {
          id: 'ago-kokuritsu',
          x: 0.76,
          y: 0.3,
          title: '伊勢志摩国立公園',
          body: '伊勢志摩国立公園の指定は昭和21年11月20日、面積は55,544ヘクタール。関係市町村は伊勢市・鳥羽市・志摩市・度会郡南伊勢町です。',
          source: 'https://www.env.go.jp/park/iseshima/intro/index.html',
          mapQuery: '伊勢志摩国立公園',
        },
      ],
    },
  ],
};
